import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { ValidationError } from '../../components';

import css from './FieldBrandCombobox.module.css';

// The enum option that means "not in the list" — selecting it reveals the
// free-text brand field (see EditListingDetailsForm).
export const OTHER_OPTION = 'annet';
export const FALLBACK_OPTION_ID = '__suggest__';

/**
 * Case-insensitive substring match, whitespace-tolerant. "veil" matches
 * "Vetements" and "Veja" alike; " holz " matches "Holzweiler".
 */
export const filterOptions = (options, query) => {
  const q = (query || '').trim().toLowerCase();
  if (!q) {
    return options;
  }
  return options.filter(o => o.label.toLowerCase().includes(q));
};

/**
 * Brand picker: a hand-written combobox (WAI-ARIA authoring practices,
 * "editable combobox with list autocomplete") over the configured brand
 * options. No library — the list is ~100 items and every combobox package
 * brings its own CSS that fights the field styles this has to match.
 *
 * Behaviour:
 *  - focus/click opens the full list; typing filters it as you type
 *  - Arrow keys move the highlight, Enter selects, Escape closes, Tab
 *    selects the highlighted item and moves on
 *  - when nothing matches, the only option is "Fant du ikke merket?", which
 *    selects `annet` so the free-text field appears
 *  - a live region announces the number of matches / the fallback
 *
 * The value stored is the option KEY (as the plain select did), so nothing
 * downstream changes.
 */
const FieldBrandComboboxComponent = props => {
  const {
    rootClassName,
    className,
    id,
    label,
    placeholder,
    options,
    input,
    meta,
    disabled,
  } = props;
  const intl = useIntl();
  const listboxId = `${id}-listbox`;
  const liveId = `${id}-live`;
  const reactId = useId();

  const selected = options.find(o => o.key === input.value) || null;
  const [isOpen, setIsOpen] = useState(false);
  // What's in the text box. Kept separate from the form value: while the
  // list is open it's the filter; once closed it shows the selected label.
  const [query, setQuery] = useState(selected ? selected.label : '');
  const [activeIndex, setActiveIndex] = useState(-1);
  // Opening the list shows EVERYTHING, even when a brand is already chosen
  // and its label fills the box — otherwise changing your mind would show a
  // one-item list. Filtering starts with the first keystroke.
  const [hasTyped, setHasTyped] = useState(false);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  // Prefill / external change (e.g. the AI suggestion landing): mirror the
  // selected label into the box whenever the list is closed.
  useEffect(() => {
    if (!isOpen) {
      setQuery(selected ? selected.label : input.value === OTHER_OPTION ? '' : '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.value, isOpen]);

  const matches = useMemo(() => filterOptions(options, isOpen && hasTyped ? query : ''), [
    options,
    query,
    isOpen,
    hasTyped,
  ]);
  const showFallback = isOpen && matches.length === 0;
  // Items as rendered: the matches, or the single fallback row.
  const items = showFallback
    ? [{ key: FALLBACK_OPTION_ID, label: intl.formatMessage({ id: 'FieldBrandCombobox.suggest' }) }]
    : matches;
  const optionDomId = index => `${listboxId}-opt-${index}`;

  const open = () => {
    setIsOpen(true);
    setActiveIndex(-1);
    setHasTyped(false);
  };
  const close = () => {
    setIsOpen(false);
    setActiveIndex(-1);
    setHasTyped(false);
  };

  const commit = item => {
    if (!item) {
      return;
    }
    const nextValue = item.key === FALLBACK_OPTION_ID ? OTHER_OPTION : item.key;
    input.onChange(nextValue);
    close();
    if (nextValue !== OTHER_OPTION) {
      setQuery(item.label);
    } else {
      // The free-text field takes over from here; leave the box empty so it
      // doesn't read as if "Annet" were a brand.
      setQuery('');
    }
  };

  const onInputChange = e => {
    const next = e.target.value;
    setQuery(next);
    setHasTyped(true);
    if (!isOpen) {
      setIsOpen(true);
    }
    setActiveIndex(-1);
    // Typing over a chosen brand un-chooses it until something is picked
    // again — otherwise the box could show "Ganni" while the value is Rains.
    if (selected && next !== selected.label) {
      input.onChange('');
    }
  };

  const onKeyDown = e => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          open();
          setActiveIndex(0);
        } else {
          setActiveIndex(i => (items.length ? (i + 1) % items.length : -1));
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!isOpen) {
          open();
          setActiveIndex(items.length - 1);
        } else {
          setActiveIndex(i => (items.length ? (i <= 0 ? items.length - 1 : i - 1) : -1));
        }
        break;
      case 'Enter':
        if (isOpen) {
          e.preventDefault();
          if (activeIndex >= 0) {
            commit(items[activeIndex]);
          } else if (items.length === 1) {
            commit(items[0]);
          }
        }
        break;
      case 'Escape':
        if (isOpen) {
          e.preventDefault();
          close();
          setQuery(selected ? selected.label : '');
        }
        break;
      case 'Tab':
        // Leave the field with the highlighted item chosen; don't trap focus.
        if (isOpen && activeIndex >= 0) {
          commit(items[activeIndex]);
        } else if (isOpen) {
          close();
          setQuery(selected ? selected.label : '');
        }
        break;
      default:
        break;
    }
  };

  const onBlur = e => {
    // Clicks on options are handled with onMouseDown (fires before blur), so
    // by the time focus leaves the box we can safely tidy up.
    if (rootRef.current && rootRef.current.contains(e.relatedTarget)) {
      return;
    }
    close();
    setQuery(selected ? selected.label : '');
    input.onBlur();
  };

  const { touched, invalid, error } = meta;
  const hasError = touched && invalid && error;

  const liveMessage = !isOpen
    ? ''
    : showFallback
    ? intl.formatMessage({ id: 'FieldBrandCombobox.liveNoMatch' })
    : intl.formatMessage({ id: 'FieldBrandCombobox.liveMatches' }, { count: matches.length });

  return (
    <div className={classNames(rootClassName || css.root, className)} ref={rootRef}>
      {label ? (
        <label htmlFor={id} className={classNames(css.label, { [css.labelError]: hasError })}>
          {label}
        </label>
      ) : null}
      <div className={css.control}>
        <input
          ref={inputRef}
          id={id}
          name={input.name}
          type="text"
          role="combobox"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck="false"
          className={classNames(css.input, { [css.inputError]: hasError })}
          placeholder={placeholder}
          value={query}
          disabled={disabled}
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-activedescendant={isOpen && activeIndex >= 0 ? optionDomId(activeIndex) : undefined}
          aria-invalid={hasError ? true : undefined}
          onFocus={e => {
            if (!isOpen) {
              open();
            }
            // Select the current label so typing replaces it.
            e.target.select();
            input.onFocus();
          }}
          onClick={() => {
            if (!isOpen) {
              open();
            }
          }}
          onChange={onInputChange}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
        />
        <span className={css.chevron} aria-hidden="true" />
        <ul
          id={listboxId}
          role="listbox"
          aria-label={typeof label === 'string' ? label : undefined}
          className={classNames(css.listbox, { [css.listboxOpen]: isOpen })}
          hidden={!isOpen}
        >
          {items.map((item, index) => {
            const isActive = index === activeIndex;
            const isSelected = item.key === input.value;
            const isFallback = item.key === FALLBACK_OPTION_ID;
            return (
              <li
                key={item.key}
                id={optionDomId(index)}
                role="option"
                aria-selected={isSelected}
                className={classNames(css.option, {
                  [css.optionActive]: isActive,
                  [css.optionSelected]: isSelected,
                  [css.optionFallback]: isFallback,
                })}
                onMouseDown={e => {
                  // Before blur, so the click lands on the option not on a
                  // list that has just been closed.
                  e.preventDefault();
                  commit(item);
                }}
                onMouseMove={() => {
                  if (!isActive) {
                    setActiveIndex(index);
                  }
                }}
              >
                {item.label}
              </li>
            );
          })}
        </ul>
      </div>
      <span id={liveId} className={css.live} role="status" aria-live="polite" key={reactId}>
        {liveMessage}
      </span>
      <ValidationError fieldMeta={meta} />
    </div>
  );
};

/**
 * Final Form field wrapping the brand combobox.
 *
 * @param {Object} props
 * @param {string} props.name - form field name
 * @param {string} props.id
 * @param {React.Node} [props.label]
 * @param {string} [props.placeholder]
 * @param {Array<{key: string, label: string}>} props.options
 * @param {Function} [props.validate]
 */
const FieldBrandCombobox = props => {
  const { name, validate, ...rest } = props;
  return <Field name={name} validate={validate} component={FieldBrandComboboxComponent} {...rest} />;
};

export default FieldBrandCombobox;
